import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaFilePdf, FaFileWord, FaFile } from "react-icons/fa";
import { RxCross2 } from "react-icons/rx";
import style from "./style.module.css";
import Button from "../button/Button";
import { getMyAgreementPdfUrl, getPublicAgreementTemplate } from "../../api/HttpApis";
import { showToast } from "../../utils/Toastify/Toastify";

const DetailsSection = ({ title, rows }) => {
  const visibleRows = rows.filter(([, value]) => value !== null && value !== undefined && value !== "");
  if (visibleRows.length === 0) return null;
  return (
    <div className={style.reviewDetailsCard}>
      <h3 className={style.reviewDetailsTitle}>{title}</h3>
      <div className={style.reviewDetailsGrid}>
        {visibleRows.map(([label, value]) => (
          <div key={label} className={style.reviewDetailsRow}>
            <span className={style.reviewDetailsLabel}>{label}</span>
            <span className={style.reviewDetailsValue}>{value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : null);
const isImage = (doc) =>
  doc.mime_type?.startsWith("image/") || /\.(png|jpe?g|gif|webp)$/i.test(doc.filename || "");

const FileTypeIcon = ({ doc }) => {
  if (doc.mime_type === "application/pdf" || /\.pdf$/i.test(doc.filename || "")) {
    return <FaFilePdf className={style.reviewDocumentIcon} />;
  }
  if (/\.docx?$/i.test(doc.filename || "") || doc.mime_type?.includes("word")) {
    return <FaFileWord className={style.reviewDocumentIcon} />;
  }
  return <FaFile className={style.reviewDocumentIcon} />;
};

// Shown alongside the under-review notice so a pending/rejected creator can
// see the complete application they submitted at signup — the same basic
// info, restrictions, and
// documents collected across CreatorOnboarding.js's steps — without needing
// a separate page. `profile` is the creatorGetProfile() response fetched once
// by MainLayout (which already needs it for the review status) and handed
// down here, so this component doesn't issue a duplicate request. Best-effort:
// while it's still loading, the review notice above still stands alone.
const CreatorSignupDetails = ({ profile, onRefresh }) => {
  const navigate = useNavigate();
  // Only images get an inline lightbox preview — PDFs/Word docs open in a new
  // tab instead, since previewing those inline would need a viewer library.
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewingAgreement, setPreviewingAgreement] = useState(false);
  // blob: URL for the in-page agreement popup below — revoked on close.
  const [agreementPdfUrl, setAgreementPdfUrl] = useState(null);
  const [downloadingAgreement, setDownloadingAgreement] = useState(false);

  const details = profile;
  if (!details) return null;

  // The admin-uploaded template (Documents page) — the same agreement the
  // creator previewed at signup (CreatorOnboarding.js). Null when none is
  // uploaded, in which case we fall back to the generated PDF.
  const fetchAgreementTemplateUrl = async () => {
    try {
      const res = await getPublicAgreementTemplate();
      return res?.data?.data?.url || null;
    } catch {
      return null;
    }
  };

  const handlePreviewAgreement = async () => {
    setPreviewingAgreement(true);
    try {
      const templateUrl = await fetchAgreementTemplateUrl();
      if (templateUrl) {
        setAgreementPdfUrl(
          /\.docx?$/i.test(templateUrl)
            ? `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(templateUrl)}`
            : templateUrl,
        );
        return;
      }
      const url = await getMyAgreementPdfUrl();
      setAgreementPdfUrl(url);
    } catch (error) {
      showToast(
        error?.response?.data?.message || "Couldn't open the agreement PDF",
        "error",
      );
    } finally {
      setPreviewingAgreement(false);
    }
  };

  // Same PDF endpoint as the preview — once both parties have signed it
  // renders with both signatures embedded and a "Fully executed" footer.
  const handleDownloadAgreement = async () => {
    setDownloadingAgreement(true);
    try {
      const templateUrl = await fetchAgreementTemplateUrl();
      if (templateUrl) {
        window.open(templateUrl, "_blank", "noopener,noreferrer");
        return;
      }
      const url = await getMyAgreementPdfUrl();
      const link = document.createElement("a");
      link.href = url;
      link.download = "agreement.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (error) {
      showToast(
        error?.response?.data?.message || "Couldn't download the agreement",
        "error",
      );
    } finally {
      setDownloadingAgreement(false);
    }
  };

  const closeAgreementPreview = () => {
    if (agreementPdfUrl?.startsWith("blob:")) window.URL.revokeObjectURL(agreementPdfUrl);
    setAgreementPdfUrl(null);
  };

  const signup = details.signup_details;
  const license = signup?.license_terms;
  const revenue = signup?.revenue_share;
  const restrictions = signup?.restrictions;
  const documents = signup?.documents || [];
  const adminReview = signup?.admin_review;

  return (
    <>
      {/* Set only when an admin has edited the split/threshold after
          signup (CreatorDetail.js "02 Revenue share") — same "admin left a
          note" pattern as the Admin feedback card below, just for this
          specific action instead of a correction request. First card on the
          page so it's the first thing a returning creator sees. Hidden once
          Approved — this component is also reused on the always-visible
          CreatorProfile page, where a one-time "terms changed" notice from
          the review process would otherwise linger indefinitely. */}
      {revenue?.updated_at && details.status !== "Approved" && (
        <div className={style.reviewDetailsCard} style={{ borderLeft: "4px solid #c99a5b" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
            <div>
              <h3 className={style.reviewDetailsTitle}>Revenue share updated</h3>
              <p className={style.reviewDetailsValue} style={{ marginTop: 8, marginBottom: revenue.update_note ? 8 : 0 }}>
                {process.env.REACT_APP_TITLE} updated your revenue share terms on {formatDate(revenue.updated_at)}.
              </p>
              {revenue.update_note && (
                <p className={style.reviewDetailsValue} style={{ whiteSpace: "pre-wrap" }}>
                  {revenue.update_note}
                </p>
              )}
            </div>
            {/* Once admin has submitted the agreement for signature, the
                creator should go sign it rather than resubmit/change the
                application — hide the action, keep the notice. */}
            {!signup?.agreement_status?.sent_at && (
              <Button onClick={() => navigate("/creator-edit-application")}>
                Resubmit / Change Application
              </Button>
            )}
          </div>
        </div>
      )}

      {adminReview && (
        <div className={style.reviewDetailsCard} style={{ borderLeft: "4px solid #c99a5b" }}>
          <h3 className={style.reviewDetailsTitle}>Admin feedback</h3>
          <p className={style.reviewDetailsValue} style={{ whiteSpace: "pre-wrap", marginBottom: 20 }}>
            {adminReview.note}
          </p>
          <Button onClick={() => navigate("/creator-edit-application")}>
            Edit & Resubmit
          </Button>
        </div>
      )}

      <DetailsSection
        title="Your submitted details"
        rows={[
          ["Name", details.name],
          ["Email", details.email],
          ["Phone", details.phone],
          ["Channel name", details.channel?.channel_name],
          ["Channel category", details.channel?.channel_category],
          ["Channel description", details.channel?.channel_description],
          ["Bio", details.bio],
          ["Submitted", formatDate(details.created_at)],
        ]}
      />

      {license && (
        <div className={style.reviewDetailsCard}>
          <h3 className={style.reviewDetailsTitle}>Your agreement</h3>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <Button onClick={handlePreviewAgreement} loading={previewingAgreement}>
              Preview Agreement
            </Button>
            <Button variant="outline" onClick={handleDownloadAgreement} loading={downloadingAgreement}>
              Download Agreement
            </Button>
          </div>
        </div>
      )}

      <DetailsSection
        title="Restrictions and notes"
        rows={[
          ["Media description", restrictions?.media_description],
          ["Rights restrictions", restrictions?.rights_restrictions],
          [`Message to ${process.env.REACT_APP_TITLE}`, restrictions?.message_to_platform],
        ]}
      />

      {documents.length > 0 && (
        <div className={style.reviewDetailsCard}>
          <h3 className={style.reviewDetailsTitle}>Documents</h3>
          <div className={style.reviewDocumentsList}>
            {documents.map((doc) => {
              const image = isImage(doc);
              const openDoc = () =>
                image ? setPreviewDoc(doc) : window.open(doc.url, "_blank", "noreferrer");
              return (
                <div
                  key={doc.id}
                  className={style.reviewDocumentItem}
                  role="button"
                  tabIndex={0}
                  onClick={openDoc}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") openDoc();
                  }}
                >
                  {image ? (
                    <img src={doc.url} alt={doc.filename} className={style.reviewDocumentThumb} />
                  ) : (
                    <FileTypeIcon doc={doc} />
                  )}
                  <div className={style.reviewDocumentInfo}>
                    <span className={style.reviewDetailsLabel}>{doc.category}</span>
                    <span className={style.reviewDetailsValue}>{doc.filename}</span>
                  </div>
                  <span className={style.reviewDocumentAction}>Preview</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {previewDoc && (
        <div className={style.reviewLightboxOverlay} onClick={() => setPreviewDoc(null)}>
          <div className={style.reviewLightboxBody} onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className={style.reviewLightboxClose}
              onClick={() => setPreviewDoc(null)}
              aria-label="Close preview"
            >
              <RxCross2 />
            </button>
            <img src={previewDoc.url} alt={previewDoc.filename} className={style.reviewLightboxImage} />
            <p className={style.reviewLightboxCaption}>{previewDoc.filename}</p>
          </div>
        </div>
      )}

      {agreementPdfUrl && (
        <div className={style.reviewLightboxOverlay} onClick={closeAgreementPreview}>
          <div className={style.reviewLightboxBody} onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className={style.reviewLightboxClose}
              onClick={closeAgreementPreview}
              aria-label="Close preview"
            >
              <RxCross2 />
            </button>
            <iframe src={agreementPdfUrl} title="Agreement preview" className={style.reviewLightboxPdf} />
          </div>
        </div>
      )}
    </>
  );
};

export default CreatorSignupDetails;
